import React, { useState, useEffect } from "react";
import { uploadAgency, uploadFile } from "../../DAL/create"; // Assuming `fetchAgencyDetail` is imported here
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAgencyDetail } from "../../DAL/fetch";
import { updateAgency } from "../../DAL/update";
import { fileUrl } from "../../Config/Config";
import { enqueueSnackbar } from "notistack";
function EditAgency() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileURL, setFileURL] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");

  const navigate = useNavigate();
  const { id } = useParams();

  // Handle File Change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : "");
    setFileUploaded(false);
  };

  // Fetch agency data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchAgencyDetail(id);

        if (response && response.data) {
          const data = response.data.data || response.data;

          // Set form data
          setName(data.name || "");
          setCountry(data.country || "");
          setEmail(data.email || "");
          setPhone(data.phone || "");
          setAddress(data.address || "");
          setUserName(data.userName || "");
          setPassword(data.password || "");
          setBankName(data.bankName || "");
          setBankAccountName(data.bankAccountName || "");

          if (data.picture) {
            const updatedURL = data.picture.startsWith("http")
              ? data.picture
              : `${fileUrl}${data.picture}`;
            setFileURL(updatedURL);
          }
        }
      } catch (error) {
        console.error("Failed to fetch agency details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle File Upload
  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      enqueueSnackbar("Please select a file first!", { variant: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setLoading(true);
    try {
      const imagedata = await uploadFile(formData);
      setFileURL(imagedata.data.url);
      setFileUploaded(true);
      enqueueSnackbar("File uploaded successfully!", { variant: "success" });
    } catch (error) {
      console.error("Error uploading file:", error);
      enqueueSnackbar("Failed to upload file. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name) {
      enqueueSnackbar("Agency name field is required", { variant: "error" });
      return;
    }
    if (!email) {
      enqueueSnackbar("Email field is required", { variant: "error" });
      return;
    }

    const payload = {
      picture: fileURL,
      name,
      country,
      email,
      phone,
      address,
      userName,
      password,
      bankName,
      bankAccountName,
    };

    setLoading(true);
    try {
      const resp = await updateAgency(id, payload);
      if (resp.status === 200 || resp?.status === 201) {
        enqueueSnackbar("Agency updated successfully!", { variant: "success" });
        navigate("/agencylist");
      } else {
        enqueueSnackbar(
          resp?.message[0]?.constraints ||
            resp?.error ||
            resp?.message ||
            resp?.data?.message ||
            resp?.data?.error,
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error updating agency:", error);
      enqueueSnackbar("Failed to update agency. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        maxWidth: 400,
        margin: "auto",
        mt: 4,
      }}
    >
      <Typography variant="h6" component="h2">
        Edit Agency
      </Typography>

      {fileURL && (
        <Box sx={{ mb: 2 }}>
          <img
            src={fileURL}
            alt="Agency"
            style={{ width: "100%", borderRadius: 8 }}
          />
        </Box>
      )}

      <TextField
        fullWidth
        required
        label="Agency Name"
        variant="outlined"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      {/* Other form fields */}
      <TextField
        fullWidth
        required
        label="Country"
        variant="outlined"
        value={country}
        onChange={(e) => setCountry(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Email"
        variant="outlined"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Phone"
        variant="outlined"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Address"
        variant="outlined"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Username"
        variant="outlined"
        value={userName}
        onChange={(e) => setUserName(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Password"
        type="password"
        variant="outlined"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Bank Name"
        variant="outlined"
        value={bankName}
        onChange={(e) => setBankName(e.target.value)}
      />
      <TextField
        fullWidth
        required
        label="Bank Account Name"
        variant="outlined"
        value={bankAccountName}
        onChange={(e) => setBankAccountName(e.target.value)}
      />

      {/* Upload Section */}
      <Typography variant="h6">Upload Picture</Typography>
      <Box sx={{ width: "100%", display: "flex", gap: 2 }}>
        <input type="file" accept="image/*" onChange={handleFileChange} />
        <Button
          variant="contained"
          color={fileUploaded ? "success" : "primary"}
          onClick={handleFileSubmit}
          disabled={loading || !file}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : fileUploaded ? (
            "Uploaded"
          ) : (
            "Upload"
          )}
        </Button>
      </Box>

      {fileName && <Typography variant="body2">{fileName}</Typography>}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        sx={{ textTransform: "none" }}
        fullWidth
      >
        Update Agency
      </Button>
    </Box>
  );
}

export default EditAgency;
